// Services
import breedService from "@/app/services/main/breed/breed-service.js";
import damSireService from "@/app/services/main/dam-sire/dam-sire-service.js";

// Utils
import { vuelidateUtil } from "@/utils/vuelidate-util.js";

// Mixins
// import { GoogleAnalyticsMixins } from '@/app/mixins/utils/google-analytics-mixins';
import { dateMixins } from "@/app/mixins/utils/date-mixins";
import { toastMessageMixins } from "@/app/mixins/utils/toast-message-mixins";
import { imageCompressorMixins } from "@/app/mixins/utils/image-compressor-mixins";
import { inputTextMixins } from "@/app/mixins/utils/input-text-mixins";
import { userMixins } from "@/app/mixins/modules/user-mixins.js";
import { errorFocusMixins } from "@//app/mixins/utils/error-focus-mixins.js";

// Components
const FileUpload = () =>
    import(
        /* webpackPrefetch: true, webpackChunkName: "file-upload" */ "@/app/views/main-view/_shared/_components/file-upload/file-upload.vue"
    );

// Third Party libraries
import DatePicker from "vue2-datepicker";

// Constants
import { STATUS_CODES, SUCCESS_MSG, WHELPING_DATE_STATUS } from "@/app/constants/status-constants";
import { BREEDER_ROUTES } from '@/app/constants/breeder-routes-constants';

const DOG_GENDER = {
    DAM: "Dam",
    SIRE: "Sire",
}

const WHELP_TYPES_VALIDITY = {
    "natural": {
        label: "natural",
        maxCount: 8
    }, 
    "c-section": {
        label: "c-section",
        maxCount: 3
    }, 
}

const MAX_PREV_LITTERS_COUNT = 8;
const MAX_TOTAL_WHELP_TYPES_COUNT = 8;

export default {
    name: "EditDamSireContainer",
    components: {
        FileUpload,
        DatePicker,
    },
    mixins: [
        userMixins,
        imageCompressorMixins,
        inputTextMixins,
        toastMessageMixins,
        dateMixins,
        errorFocusMixins,
    ],
    data() {
        return {
            DAM_SIRE_LIST_PATH: BREEDER_ROUTES.DAM_SIRE_LIST,

            damSireId: null,
            imageTypeList: ['image/svg','image/png','image/jpg','image/jpeg','image/gif','image/raw'],
            isAPILoading: false,
            isAPISubmitLoading: false,
            imageToCrop: "",

            breedList: [],
            genderList: [DOG_GENDER.DAM, DOG_GENDER.SIRE],

            dogImageUploadPreviewSource: "",
            dogImageToCrop: "",
            dogImageUpload: "",

            filesToRemove: [],

            inputFields: {
                dogName: "",
                breed: "",
                gender: "",
                dateOfBirth: "",
            },

            previousWhelpingDates: [],
            previousWhelpingDatesToRemove: [],

            dogNotes: "",
            dogNotesHistory: [],

            initialValues: {},

            whelpTypes: {
                naturalCount: 0,
                csectionCount: 0,
            },

            MAX_PREV_LITTERS_COUNT,
            initialPrevLittersWhelpDates: [],
            prevLittersWhelpDates: [],
            isShowMorePrevLittersToggled: false,
            isPrevLittersExceedMaxCount: false,

            prevRoute: 'Dam/Sire List',
            prevRouteTo: BREEDER_ROUTES.DAM_SIRE_LIST,
        };
    },
    computed: {
        validationRules() {
            let fields = {
                inputFields: {
                    dogName: {},
                    breed: {},
                    gender: {},
                    dateOfBirth: {},
                },
            };

            fields.inputFields = vuelidateUtil.setFieldsRequired(fields.inputFields, true);

            return fields;
        },
        dogImageSource() {
            let img = "";

            if (this.dogImageUploadPreviewSource) {
                img = this.dogImageUploadPreviewSource;
            } else {
                img = require("@/assets/images/_shared/default-profile-pic.png");
            }

            return img;
        },
        isGenderDam(){
            return this.inputFields.gender === DOG_GENDER.DAM;
        },
    },
    async created() {
        if(this.$route.query){
            this.damSireId = this.$route.query.id;

            if (this.$route.query.from === "dam-sire-list") {
                this.prevRoute = "Dam/Sire List";
                this.prevRouteTo = BREEDER_ROUTES.DAM_SIRE_LIST;
            }
        }

        await this.fetchDetails();
 
    },
    methods: {
        async fetchDetails(){
            try{
                this.isAPILoading = true;
                await this.getBreedList();
                await this.getDamSireDetails();
                await this.getDamWhelpTypesAndPrevLitters();
            } catch(err){
                this.showGenericErrorToast();
            } finally {
                this.isAPILoading = false;
            }
        },
        async onUpdateDetails() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                this.setFocusOnFirstErrorField();
                return;
            }

            this.isAPISubmitLoading = true;

            /*
            // filter out whelping dates that are saved in the DB by checking if their status is "ACTIVE"
            const emptyExistingWhelpingDates = this.previousWhelpingDates.filter(({ status, date }) => {
                return status === WHELPING_DATE_STATUS.ACTIVE && !date;
            });

            const emptyExistingWhelpingDatesIds = emptyExistingWhelpingDates.length ? emptyExistingWhelpingDates.map(({ id }) => id) : [];
            this.previousWhelpingDatesToRemove = [...this.previousWhelpingDatesToRemove, ...emptyExistingWhelpingDatesIds];

            // remove added whelping datepickers with null values
            this.previousWhelpingDates = this.previousWhelpingDates.filter(({date}) => !!date);

            // format dates to properly save them (not index 0)
            const formattedDates = this.previousWhelpingDates.map((dateObj) => ({...dateObj, date: this.dateToISO(dateObj.date)}));
          
            
            // only add the previous whelping dates if the dog gender is a "Dam"
            const whelpingDates = this.inputFields.gender === DOG_GENDER.DAM ? formattedDates : []
            */

            try {
                const dateOfBirth = this.dateToISO(this.inputFields.dateOfBirth);
                const dogImageUploadSource = this.dogImageUpload && this.dogImageUpload.length ? this.dogImageUpload[0] : null;

                const damSireDetails = {
                    formData: {
                        ...this.inputFields,
                        dateOfBirth,
                        filesToRemove: this.filesToRemove,
                        dogNotes: this.dogNotes,
                    },
                    dogImageUpload: dogImageUploadSource,
                };

                const response = await damSireService.updateDamSire(this.damSireId, damSireDetails);

                const { SUCCESS_UPDATE_DAM_SIRE, ERROR_UPDATE_DAM_SIRE } = this.toastMessages.damSire();

                if (response.status === STATUS_CODES.SUCCESS_NO_CONTENT) {
                    this.showCustomSuccessToast(SUCCESS_MSG.SUCCESS_TITLE, SUCCESS_UPDATE_DAM_SIRE);

                    await this.getDamSireDetails();
                    // this.$router.push(this.DAM_SIRE_LIST_PATH)
                    
                } else {
                    this.showAPIErrorToast(ERROR_UPDATE_DAM_SIRE, response);
                }
                
            } catch (err) {
                this.showGenericErrorToast();
            } finally {
                this.isAPISubmitLoading = false;
            }
        },
        async getDamSireDetails(){
            try{
                // this.isAPILoading = true;
                const damSireId = this.$route.query.id;
                const detailsResponse = await damSireService.getDamSireDetails(damSireId);

                if (detailsResponse.status === STATUS_CODES.SUCCESS) {

                    const { dogName, gender, breedId, birthdate, imageUrl, files, previousWhelpingDates, noteHistory } = detailsResponse.data;

                    const selectedBreed = this.breedList.find(breed => breed.id === breedId); 

                    this.inputFields = {
                        dogName,
                        gender,
                        dateOfBirth: new Date(birthdate),
                        breed: selectedBreed
                    }

                    // if(gender === DOG_GENDER.DAM){
                    //     this.previousWhelpingDates = previousWhelpingDates.map(data => {
                    //         return { ...data, date: new Date(data.whelpingDate) };
                    //     });
                    // }

                    this.dogImageUploadPreviewSource = imageUrl;
                    this.dogImageUpload = imageUrl;

                    if(noteHistory && noteHistory.length > 0){
                        this.dogNotesHistory = noteHistory.filter(noteObj => noteObj.note !== "");
                        this.dogNotes = noteHistory[0].note;
                    }

                    this.initialValues = {
                        dogName,
                        gender,
                        dateOfBirth: new Date(birthdate),
                        breed: selectedBreed,
                        dogImageUpload: this.dogImageUpload,
                        // previousWhelpingDates: JSON.parse(JSON.stringify(this.previousWhelpingDates)),
                        dogNotes: this.dogNotes
                    }

                } else {
                    const { ERROR_GET_DAM_SIRE } = this.toastMessages.damSire();
                    this.showAPIErrorToast(ERROR_GET_DAM_SIRE, detailsResponse);
                }
                
                
            } catch(err){
                this.showGenericErrorToast();
            } finally{
                // this.isAPILoading = false;
            }
        },
        async getDamWhelpTypesAndPrevLitters() {
            if (!this.isGenderDam) return;

            try {
                const damId = this.$route.query.id;
                const detailsResponse = await damSireService.getDamWhelpTypesAndPrevLitters(damId);
                if (detailsResponse.status === STATUS_CODES.SUCCESS) {
                    this.whelpTypes = detailsResponse.data.whelpTypes;

                    const prevLittersWhelpDates = detailsResponse.data.prevLittersWhelpDates;
                    this.prevLittersWhelpDates = Array.isArray(prevLittersWhelpDates) ? prevLittersWhelpDates.slice(0, MAX_PREV_LITTERS_COUNT) : [];
                    this.initialPrevLittersWhelpDates = prevLittersWhelpDates;

                    this.isPrevLittersExceedMaxCount = prevLittersWhelpDates.length > MAX_PREV_LITTERS_COUNT;
                } else {
                    const { ERROR_GET_DAM_PREV_LITTERS_WHELP_TYPES_COUNT } = this.toastMessages.damSire();
                    this.showAPIErrorToast(ERROR_GET_DAM_PREV_LITTERS_WHELP_TYPES_COUNT, detailsResponse);
                }
            } catch (err) {
                this.showGenericErrorToast();
            }
        },
        checkWhelpTypeCountValidity(type) {
            // if (!this.whelpTypes && !Object.keys(this.whelpTypes).length) {
            //     return false;
            // }

            const { csectionCount, naturalCount } = this.whelpTypes;

            // checking for individual types
            const { label, maxCount: typeMaxCount } = WHELP_TYPES_VALIDITY[type];
            const selectedTypeCount = label === 'natural' ? naturalCount : csectionCount;

            if (selectedTypeCount >= typeMaxCount) {
                return true;
            }

            // checking for the sum of two whelp types
            const totalCount = naturalCount + csectionCount;
            if (totalCount >= MAX_TOTAL_WHELP_TYPES_COUNT) {
                return true;
            }

            return false;
        },
        // addWhelpingDate() {
        //     const newWhelpingDate = {
        //         id: Math.random().toString(),
        //         date: null,
        //     };

        //     this.previousWhelpingDates.push(newWhelpingDate);
        // },
        // removeWhelpingDate(whelpingDate) {
        //     if(!whelpingDate) {
        //         return;
        //     }

        //     this.previousWhelpingDates = this.previousWhelpingDates.filter(
        //         (dateObj) => dateObj.id !== whelpingDate.id
        //     );

        //     if(whelpingDate.id && whelpingDate.status === WHELPING_DATE_STATUS.ACTIVE){
        //         this.previousWhelpingDatesToRemove.push(whelpingDate.id);
        //     }

        // },
        sortAlphabetically(items) {
            const sortedItems = items.sort(function (a, b) {
                return a.name.localeCompare(b.name);
            });
            return sortedItems;
        },
        async getBreedList() {
            try {
                // this.isAPILoading = true;

                const breedsResponse = await breedService.getBreeds();

                if (breedsResponse.status === STATUS_CODES.SUCCESS) {
                    const otherBreedOptions = breedsResponse.data.data.slice(
                        0,
                        4
                    );
                    const breeds = this.sortAlphabetically(
                        breedsResponse.data.data.slice(
                            4,
                            breedsResponse.data.count
                        )
                    );

                    // remove "Breed not specified", it has id = 0
                    this.breedList = [...otherBreedOptions, ...breeds].filter(
                        (breed) => breed.id != 0
                    );
                } else {
                    const { ERROR_GET_BREED_LIST } = this.toastMessages.breed();
                    this.showAPIErrorToast(
                        ERROR_GET_BREED_LIST,
                        breedsResponse
                    );
                }
            } catch (err) {
                this.showGenericErrorToast();
            } finally{
                // this.isAPILoading = false;
            }
        },
        setImageCrop({ image, blob }) {
            this.dogImageUploadPreviewSource = image;
            this.dogImageToCrop = blob;
            this.dogImageUpload = blob;
        },
        async onUploadImage(event) {
            let valid = await this.validateImageWithType(event.target.files[0], this.imageTypeList);

            // do nothing when user cancels Upload
            if (!valid) {
                // reset uploader
                this.$refs.imgUploader.value = "";
                return;
            }

            // compress image if more than 2mb
            const file = await this.compressImage(event.target.files[0]);

            this.dogImageToCrop = [file];

            // show preview of pet image
            let reader = new FileReader();

            reader.onload = () => {
                this.imageToCrop = reader.result;
                this.$refs.modalFileUpload.show();
            };

            // this.sendEventTrackingToGA(GA.REGISTRATION_PAGE.ADD_PET_IMAGE.ACTION, GA.REGISTRATION_PAGE.CATEGORY, GA.REGISTRATION_PAGE.ADD_PET_IMAGE.LABEL);
            reader.readAsDataURL(file);
        },
        clearFileInput(event) {
            event.target.value = "";
        },
        notAfterToday(date) {
            return date > new Date();
        },
        redirectToGestation(){
            if(!this.damSireId){
                return;
            }

            this.$router.push(`${BREEDER_ROUTES.EDIT_MATING}?dam=${this.damSireId}&from=dam-details`);
        },
        redirectToDocuments(){
            if(!this.damSireId){
                return;
            }

            this.$router.push(`${BREEDER_ROUTES.DAM_SIRE_DOCUMENTS}?id=${this.damSireId}`);
        },
        redirectToPrevLocation(){
            if (!this.prevRouteTo) return;

            this.$router.push(this.prevRouteTo);
        },
        redirectToAddNewMating() {
            this.$router.push(`${BREEDER_ROUTES.ADD_MATING}?dam=${this.damSireId}&from=dam-details`);
        },
        redirectToLitterList(matingId) {
            this.$router.push(`${BREEDER_ROUTES.LITTER_LIST}?id=${matingId}&from=dam-details&damId=${this.damSireId}`);
        },
        showMorePrevLitters(){
            this.isShowMorePrevLittersToggled = !this.isShowMorePrevLittersToggled;

            if(this.isShowMorePrevLittersToggled){
                this.prevLittersWhelpDates = this.initialPrevLittersWhelpDates; // show all data
            } else {
                this.prevLittersWhelpDates = [...this.prevLittersWhelpDates.slice(0, MAX_PREV_LITTERS_COUNT)];
            }
        }
    },
    beforeRouteLeave(to, from, next) {
        const { dogName, breed, gender, dateOfBirth } = this.inputFields;
        const breedId = breed ? breed.id : null;

        const isDogImageUploadChanged = this.initialValues.dogImageUpload !== this.dogImageUpload;
        // const isGenderChanged = gender !== this.initialValues.gender;
        const isBreedChanged =  breedId !== this.initialValues.breed.id;
        const isDogNameChanged = dogName !== this.initialValues.dogName;
        const isDateOfBirthChanged = new Date(dateOfBirth).getTime() !== new Date(this.initialValues.dateOfBirth).getTime();
        const isDogNotesChanged = this.dogNotes !== this.initialValues.dogNotes;

        // let isWhelpingDatesChanged = false;
        // if(this.previousWhelpingDates.length !== this.initialValues.previousWhelpingDates.length){
        //    isWhelpingDatesChanged = true;
        // } else {

        //     if((this.previousWhelpingDates.length && this.initialValues.previousWhelpingDates.length)
        //         && (this.previousWhelpingDates.length === this.initialValues.previousWhelpingDates.length)){

        //         isWhelpingDatesChanged = this.previousWhelpingDates.some((dateObj, index) => {
        //             const currentDate = new Date(dateObj.date).getTime();
        //             const initialDate = new Date(this.initialValues.previousWhelpingDates[index].date).getTime();

        //             return currentDate !== initialDate;
        //         })
        //     }
        // }

        if (isDogImageUploadChanged ||
            isBreedChanged
                || isDogNameChanged 
                || isDateOfBirthChanged 
                || isDogNotesChanged) {
            this.$swal({
                imageHeight: '30px',
                title: 'You have unsaved changes!',
                text: 'Are you sure you want to leave this page?',
                imageUrl: `${require('@/assets/images/_shared/dog-confirm.png')}`,
                confirmButtonColor: '#4B8BFF',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Confirm',
                showCancelButton: true,
                cancelButtonText: 'Cancel',
                reverseButtons: true
            })
                .then(result => {
                if (result.value) {
                    next(true);
                } else {
                    next(false);
                }
            });
        } else {
            next(true);
        }
    },
    validations() {
        return this.validationRules;
    },
};
